import React from "react";
import "../App.css";
import { fetchWithAuth } from "../utils";

function createParticipant() {
  return {
    id: `participant-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: "",
    subtitle: "",
    description: "",
    imageData: "",
    imageUrl: "",
  };
}

function TournamentAdminPage() {
  const [form, setForm] = React.useState(null);
  const [stats, setStats] = React.useState(null);
  const [message, setMessage] = React.useState("");

  const loadAdminData = React.useCallback(async () => {
    const result = await fetchWithAuth("/api/tournament/admin");
    setForm({
      title: result.tournament.title,
      subtitle: result.tournament.subtitle,
      description: result.tournament.description,
      allowVoting: result.tournament.allowVoting,
      participants: result.tournament.participants,
    });
    setStats(result.stats);
  }, []);

  React.useEffect(() => {
    loadAdminData().catch((error) => setMessage(error.message));
  }, [loadAdminData]);

  const updateParticipant = (id, field, value) => {
    setForm((prev) => ({
      ...prev,
      participants: prev.participants.map((participant) =>
        participant.id === id ? { ...participant, [field]: value } : participant
      ),
    }));
  };

  const handleParticipantFile = (id, file) => {
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      updateParticipant(id, "imageData", reader.result);
    };
    reader.readAsDataURL(file);
  };

  const saveTournament = async () => {
    try {
      await fetchWithAuth("/api/tournament/admin", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setMessage("Турнір збережено");
      await loadAdminData();
    } catch (error) {
      setMessage(error.message);
    }
  };

  if (!form || !stats) {
    return <div className="tournament-admin-page">Завантаження турніру...</div>;
  }

  return (
    <div className="tournament-admin-page">
      <section className="admin-hero">
        <div>
          <div className="admin-kicker">Tournament Admin</div>
          <h1>Керування турніром</h1>
          <p>
            Окрема сторінка для налаштування голосування, учасників, фото та
            перегляду статистики по голосах.
          </p>
        </div>
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <span>Усього голосів</span>
            <strong>{stats.totalVotes}</strong>
          </div>
          <div className="admin-stat-card">
            <span>Унікальних пристроїв</span>
            <strong>{stats.uniqueDevices}</strong>
          </div>
          <div className="admin-stat-card">
            <span>Ліміт з одного IP</span>
            <strong>{stats.ipLimit}</strong>
          </div>
          <div className="admin-stat-card">
            <span>Учасників</span>
            <strong>{form.participants.length}</strong>
          </div>
          <div className="admin-stat-card success">
            <span>Статус</span>
            <strong>{form.allowVoting ? "ON" : "OFF"}</strong>
          </div>
        </div>
      </section>

      <div className="tournament-admin-layout">
        <section className="admin-card">
          <div className="admin-card-header">
            <div>
              <div className="admin-section-kicker">Налаштування</div>
              <h2>Сторінка турніру</h2>
            </div>
          </div>

          <div className="tournament-form-grid">
            <input
              value={form.title}
              onChange={(event) => setForm({ ...form, title: event.target.value })}
              placeholder="Назва турніру"
            />
            <input
              value={form.subtitle}
              onChange={(event) => setForm({ ...form, subtitle: event.target.value })}
              placeholder="Підзаголовок"
            />
            <textarea
              value={form.description}
              onChange={(event) =>
                setForm({ ...form, description: event.target.value })
              }
              placeholder="Опис турніру"
              rows={4}
            />
            <label className="tournament-toggle">
              <input
                type="checkbox"
                checked={form.allowVoting}
                onChange={(event) =>
                  setForm({ ...form, allowVoting: event.target.checked })
                }
              />
              <span>Дозволити голосування</span>
            </label>
          </div>

          <div className="tournament-admin-toolbar">
            <button
              type="button"
              className="button"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  participants: [...prev.participants, createParticipant()],
                }))
              }
            >
              Додати учасника
            </button>
            <button type="button" className="button reserve" onClick={saveTournament}>
              Зберегти турнір
            </button>
          </div>

          {message ? <div className="tournament-alert success">{message}</div> : null}

          <div className="tournament-admin-list">
            {form.participants.map((participant) => (
              <div key={participant.id} className="tournament-admin-card">
                <input
                  value={participant.name}
                  onChange={(event) =>
                    updateParticipant(participant.id, "name", event.target.value)
                  }
                  placeholder="Ім'я / назва пари"
                />
                <input
                  value={participant.subtitle}
                  onChange={(event) =>
                    updateParticipant(participant.id, "subtitle", event.target.value)
                  }
                  placeholder="Команда / школа / категорія"
                />
                <div className="tournament-upload-block">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      handleParticipantFile(
                        participant.id,
                        event.target.files?.[0]
                      )
                    }
                  />
                  {participant.imageData ? (
                    <img
                      src={participant.imageData}
                      alt={participant.name || "Фото учасника"}
                      className="tournament-admin-preview"
                    />
                  ) : null}
                  <button
                    type="button"
                    className="button"
                    onClick={() => updateParticipant(participant.id, "imageData", "")}
                  >
                    Очистити фото
                  </button>
                </div>
                <textarea
                  value={participant.description}
                  onChange={(event) =>
                    updateParticipant(participant.id, "description", event.target.value)
                  }
                  placeholder="Короткий опис"
                  rows={3}
                />
                <button
                  type="button"
                  className="button-failer"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      participants: prev.participants.filter(
                        (item) => item.id !== participant.id
                      ),
                    }))
                  }
                >
                  Видалити учасника
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="admin-card">
          <div className="admin-card-header">
            <div>
              <div className="admin-section-kicker">Статистика</div>
              <h2>Голоси та лідери</h2>
            </div>
          </div>

          <div className="tournament-stats-list">
            {stats.participants.map((participant) => (
              <div key={participant.participantId} className="tournament-stat-row">
                <div>
                  <strong>{participant.name}</strong>
                  <span>{participant.subtitle || "Без підпису"}</span>
                </div>
                <div className="tournament-stat-badge">{participant.votes}</div>
              </div>
            ))}
          </div>

          <div className="admin-card-header">
            <div>
              <div className="admin-section-kicker">Останні голоси</div>
              <h2>Журнал</h2>
            </div>
          </div>

          <div className="tournament-votes-log">
            {stats.recentVotes.map((vote) => (
              <div key={vote.id} className="tournament-vote-row-admin">
                <strong>{vote.participantName}</strong>
                <span>{new Date(vote.createdAt).toLocaleString("uk-UA")}</span>
                <span>IP hash: {vote.ipHash.slice(0, 12) || "n/a"}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default TournamentAdminPage;
